/**
 * Kling AI Video Generation Constants
 *
 * Kling via fal.ai - mid-tier video generation provider
 *
 * The `priceNs` fields are what fal.ai bills US per clip; they match
 * `KLING_PRICING` in constants/video_generation/kling.py 1:1. The `tokensNs`
 * fields are what we charge, DERIVED from those costs through
 * `tokenBilling.ts` -- the port of `constants/pricing/base.py::cost_usd_to_billed_tokens`,
 * markup and margin floor together.
 *
 * They used to be literals computed at a 1.5x markup (525 / 735 / 840). The
 * backend markup is 2.0 and a floor now sits under it, so every Kling quote in
 * the scene picker was 25% below the charge. Deriving them means the next markup
 * change moves the quote by itself.
 */

import { videoCostUsdToTokens } from './tokenBilling';

export const KLING_MODELS = {
  TURBO: 'kling-v2.5-turbo',
  STANDARD: 'kling-v2.1-pro', // Was "kling-v2.5" which no longer exists on fal.ai
  O1: 'kling-o1',
} as const;

export type KlingModel = (typeof KLING_MODELS)[keyof typeof KLING_MODELS];

export const KLING_DURATIONS = ['5', '10'] as const;
export type KlingDuration = (typeof KLING_DURATIONS)[number];

export const KLING_ASPECT_RATIOS = ['16:9', '9:16', '1:1'] as const;
export type KlingAspectRatio = (typeof KLING_ASPECT_RATIOS)[number];

export const KLING_MODES = ['std', 'pro'] as const;
export type KlingMode = (typeof KLING_MODES)[number];

export const KLING_MODEL_OPTIONS = [
  {
    value: KLING_MODELS.TURBO,
    label: 'Turbo',
    description: 'Fast generation, great for testing',
    price5s: 0.35,
    price10s: 0.7,
    tokens5s: videoCostUsdToTokens(0.35),
    tokens10s: videoCostUsdToTokens(0.7),
    speed: '~30s',
  },
  {
    value: KLING_MODELS.STANDARD,
    label: 'Standard',
    description: 'Best quality for general use',
    price5s: 0.49,
    price10s: 0.98,
    tokens5s: videoCostUsdToTokens(0.49),
    tokens10s: videoCostUsdToTokens(0.98),
    speed: '~60s',
  },
  {
    value: KLING_MODELS.O1,
    label: 'O1 (Transitions)',
    description: 'First-to-last frame interpolation',
    price5s: 0.56,
    price10s: 1.12,
    tokens5s: videoCostUsdToTokens(0.56),
    tokens10s: videoCostUsdToTokens(1.12),
    speed: '~60s',
  },
] as const;

export const KLING_DURATION_OPTIONS = [
  { value: '5', label: '5 seconds', price: 'base' },
  { value: '10', label: '10 seconds', price: '2x base' },
] as const;

export const KLING_ASPECT_RATIO_OPTIONS = [
  { value: '16:9', label: 'Landscape (16:9)', icon: 'RectangleHorizontal' },
  { value: '9:16', label: 'Portrait (9:16)', icon: 'RectangleVertical' },
  { value: '1:1', label: 'Square (1:1)', icon: 'Square' },
] as const;

export interface KlingConfig {
  model: KlingModel;
  duration: KlingDuration;
  aspect_ratio: KlingAspectRatio;
  mode: KlingMode;
}

export const DEFAULT_KLING_CONFIG: KlingConfig = {
  model: 'kling-v2.5-turbo',
  duration: '5',
  aspect_ratio: '16:9',
  mode: 'std',
};

/** The option row for *model*, or a thrown error naming the unknown id.
 *
 * `KlingModel` is a closed union, so a miss means KLING_MODEL_OPTIONS lost a row.
 * The `if (!option) return 0` this replaces answered that bug with a free quote
 * for a clip fal.ai bills us $0.35-$1.12 for (RULE #5).
 */
function requireKlingOption(model: KlingModel): (typeof KLING_MODEL_OPTIONS)[number] {
  const option = KLING_MODEL_OPTIONS.find((m) => m.value === model);
  if (!option) {
    throw new RangeError(
      `Unknown Kling model '${model}': it has no row in KLING_MODEL_OPTIONS, so there ` +
        'is no price for it. Add the row (with constants/video_generation/kling.py) ' +
        'rather than quoting a number for a model nobody priced.'
    );
  }
  return option;
}

/**
 * fal.ai's upstream USD cost for one Kling clip.
 *
 * Every model in the table prices both durations, so this cannot be absent --
 * unlike Vidu, where a 16 s Q2 clip genuinely has no price.
 */
export function calculateKlingCost(duration: KlingDuration, model: KlingModel): number {
  const option = requireKlingOption(model);

  return duration === '10' ? option.price10s : option.price5s;
}

/**
 * Calculate estimated token cost for Kling generation
 */
export function calculateKlingTokens(duration: KlingDuration, model: KlingModel): number {
  const option = requireKlingOption(model);

  return duration === '10' ? option.tokens10s : option.tokens5s;
}

/**
 * Check if model supports first+last frame interpolation
 */
export function supportsInterpolation(model: KlingModel): boolean {
  return model === KLING_MODELS.O1;
}
